import React, { useMemo, useState } from 'react';
import { Badge, Button, EmptyState, Select, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useAuth } from '../../auth/AuthContext';
import { useI18n } from '../../i18n';
import { auth as authApi } from '../../lib/api';
import SignatureEditor from '../account/SignatureEditor';
import { LANGUAGE_SELECT_OPTIONS, normalizeLanguage } from './languages';
import './onboarding.css';

const STEPS = ['welcome', 'language', 'signature', 'journey', 'everyday', 'cheatsheet'];

/**
 * The buying journey — the one story every user must understand:
 * Ask → Approve → Order → Receive → Pay.
 */
const JOURNEY = [
  {
    icon: Icon.ClipboardList,
    step: 'Ask',
    title: 'Write down what you need',
    text: 'Running low on rice, towels, printer paper? Create a requisition — a simple list of what you need and how much.',
    analogy: 'Like a shopping list you hand to your manager.',
    where: 'Requisitions',
  },
  {
    icon: Icon.ShieldCheck,
    step: 'Approve',
    title: 'A manager says yes',
    text: 'Your list goes to the right person automatically. They review it and approve — your saved signature is stamped for you.',
    analogy: 'Like getting a thumbs-up before spending the money.',
    where: 'Approvals',
  },
  {
    icon: Icon.FileText,
    step: 'Order',
    title: 'It becomes an official order',
    text: 'The approved list turns into a purchase order (PO) that is sent to the supplier. No retyping — it happens on its own.',
    analogy: 'Like a formal “please deliver this” letter to the shop.',
    where: 'Purchase Orders',
  },
  {
    icon: Icon.Package,
    step: 'Receive',
    title: 'Tick off what arrives',
    text: 'When the delivery shows up, open the order and confirm what actually came. Stock on hand updates by itself.',
    analogy: 'Like checking the bags against the receipt before the driver leaves.',
    where: 'Receiving',
  },
  {
    icon: Icon.Receipt,
    step: 'Pay',
    title: 'Check the bill, then pay',
    text: 'The supplier’s invoice is matched against what you ordered and what arrived. If the numbers agree, it’s approved for payment.',
    analogy: 'Like comparing the bill with your receipt so you never overpay.',
    where: 'Invoices',
  },
];

/** The tools used outside the main buying journey. */
const EVERYDAY = [
  {
    icon: Icon.ShoppingCart,
    title: 'Market lists',
    text: 'For quick daily buys — the wet market, fresh produce, cash purchases. Faster than a requisition, always in your property’s local currency.',
  },
  {
    icon: Icon.Boxes,
    title: 'Stock on hand & counts',
    text: 'See what you have right now, and do regular counts to make sure the system matches the shelf.',
  },
  {
    icon: Icon.ArrowLeftRight,
    title: 'Transfers',
    text: 'Move stock between locations — say, from the main store to the kitchen — so usage is tracked to the right place.',
  },
  {
    icon: Icon.Folder,
    title: 'Catalog & suppliers',
    text: 'The item catalog is your dictionary of everything you buy; suppliers hold contacts, prices and bank details.',
  },
];

/** Idiot-proof “if this happens, do that” map. */
const CHEATSHEET = [
  { icon: Icon.ClipboardList, when: 'We’re running low on something', then: 'Create a requisition', where: 'Requisitions' },
  { icon: Icon.ShoppingCart, when: 'I’m buying fresh goods at the market today', then: 'Use a market list', where: 'Market Lists' },
  { icon: Icon.Package, when: 'A delivery just arrived at the door', then: 'Record what came in', where: 'Receiving' },
  { icon: Icon.Receipt, when: 'A supplier sent us a bill', then: 'Enter it and match it to the order', where: 'Invoices' },
  { icon: Icon.ArrowLeftRight, when: 'The kitchen needs stock from the store', then: 'Make a transfer', where: 'Transfers' },
  { icon: Icon.ListChecks, when: 'The shelf doesn’t match the system', then: 'Do a stock count', where: 'Counts' },
];

/**
 * First-run onboarding walkthrough. Shown full-screen and required (signature must be set)
 * for users who haven't completed it. Also re-openable from the account menu in `reviewMode`,
 * where it is a dismissable tour with no required actions.
 */
export default function OnboardingWalkthrough({ reviewMode = false, onClose }) {
  const { user, updateUser } = useAuth();
  const { t, setLang } = useI18n();
  const toast = useToast();

  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [language, setLanguage] = useState(() => normalizeLanguage(user?.language));

  // Live preview: the rest of the tour (and the app behind it) switches
  // immediately when a language is picked; the choice is persisted on finish.
  const chooseLanguage = (code) => {
    setLanguage(code);
    setLang(code);
  };
  const [savingSig, setSavingSig] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email;
  const hasSignature = !!user?.signatureImage;
  const isLast = stepIndex === STEPS.length - 1;

  const saveSignature = async (dataUrl) => {
    setSavingSig(true);
    try {
      const next = await authApi.updateSignature(dataUrl);
      updateUser(next);
      toast.success(t('Signature saved'));
    } catch (err) {
      toast.error(t('Could not save signature'), { description: err?.message });
    } finally {
      setSavingSig(false);
    }
  };

  const removeSignature = async () => {
    setSavingSig(true);
    try {
      const next = await authApi.updateSignature(null);
      updateUser(next);
    } catch (err) {
      toast.error(t('Could not remove signature'), { description: err?.message });
    } finally {
      setSavingSig(false);
    }
  };

  const finish = async () => {
    if (reviewMode) {
      onClose?.();
      return;
    }
    setFinishing(true);
    try {
      const next = await authApi.completeOnboarding(language);
      updateUser(next);
      toast.success(t('You’re all set — welcome aboard!'));
    } catch (err) {
      toast.error(t('Could not finish setup'), { description: err?.message });
      setFinishing(false);
    }
  };

  // On the required signature step the user cannot advance until one is saved.
  const nextBlocked = !reviewMode && step === 'signature' && !hasSignature;

  const goNext = () => {
    if (isLast) { finish(); return; }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const progress = useMemo(
    () => STEPS.map((s, i) => ({ done: i < stepIndex, current: i === stepIndex })),
    [stepIndex],
  );

  return (
    <div className="ps-scope ob-overlay" role="dialog" aria-modal="true" aria-label="Getting started">
      <div className="ob-card">
        <header className="ob-head">
          <div className="ob-head__brand">
            <span className="ob-head__mark">P</span>
            <span className="ob-head__name">Procurement</span>
          </div>
          <div className="ob-progress" aria-hidden="true">
            {progress.map((p, i) => (
              <span key={i} className={`ob-progress__dot${p.current ? ' is-current' : ''}${p.done ? ' is-done' : ''}`} />
            ))}
          </div>
          {reviewMode && (
            <Button variant="ghost" size="sm" onClick={() => onClose?.()}>{t('Close')}</Button>
          )}
        </header>

        <div className="ob-body">
          {step === 'welcome' && (
            <div className="ob-step ob-step--center">
              <span className="ob-hero-icon"><Icon.Sparkles size={30} /></span>
              <h1 className="ob-title">{t('Welcome')}{user?.firstName ? `, ${user.firstName}` : ''}</h1>
              <p className="ob-lead">
                {t('This system does one simple thing: it helps your property')}{' '}
                <strong>{t('ask for things, buy them, receive them, and pay for them')}</strong>
                {' '}{t('— without paperwork getting lost.')}
              </p>
              <p className="ob-lead">
                {t('Setup takes about two minutes: pick your language, add your signature, then a quick tour of how buying works here. No purchasing experience needed.')}
              </p>
            </div>
          )}

          {step === 'language' && (
            <div className="ob-step">
              <span className="ob-step-icon"><Icon.Globe size={22} /></span>
              <h2 className="ob-title">{t('Your language')}</h2>
              <p className="ob-lead">{t('Pick the language you’d like to use. You can change this any time in My account.')}</p>
              <div className="ob-field">
                <span className="ob-field__label">{t('Language')}</span>
                <Select
                  value={language}
                  onChange={chooseLanguage}
                  options={LANGUAGE_SELECT_OPTIONS}
                  ariaLabel="Preferred language"
                />
              </div>
            </div>
          )}

          {step === 'signature' && (
            <div className="ob-step">
              <span className="ob-step-icon"><Icon.Signature size={22} /></span>
              <h2 className="ob-title">{t('Add your signature')}</h2>
              <p className="ob-lead">
                {t('Your signature is stamped automatically when you submit or approve documents, so you never have to sign by hand. This step is required to continue.')}
              </p>

              {hasSignature ? (
                <div className="ob-sig-current">
                  <div className="ob-sig-current__head">
                    <span className="ob-sig-current__label">
                      <Icon.Check size={15} /> {t('Signature saved')}
                    </span>
                    <Badge tone="success">{t('Ready')}</Badge>
                  </div>
                  <div className="ob-sig-current__preview">
                    <img src={user.signatureImage} alt={t('Your signature')} />
                    <span className="ob-sig-current__name">{fullName}</span>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<Icon.Pencil size={24} />}
                  title={t('No signature yet')}
                  description={t('Draw it, type it, or upload an image below — then save.')}
                />
              )}

              <SignatureEditor
                initialName={fullName}
                saving={savingSig}
                onSave={saveSignature}
                onClear={hasSignature ? removeSignature : undefined}
              />
            </div>
          )}

          {step === 'journey' && (
            <div className="ob-step">
              <span className="ob-step-icon"><Icon.BookOpen size={22} /></span>
              <h2 className="ob-title">{t('How buying works — one story, five steps')}</h2>
              <p className="ob-lead">
                {t('Everything your property buys follows the same journey. Learn this once and the whole system makes sense.')}
              </p>

              <div className="ob-journey-strip" aria-hidden="true">
                {JOURNEY.map((j, i) => (
                  <React.Fragment key={j.step}>
                    {i > 0 && <span className="ob-journey-strip__arrow"><Icon.ArrowRight size={14} /></span>}
                    <span className="ob-journey-strip__chip">{t(j.step)}</span>
                  </React.Fragment>
                ))}
              </div>

              <ol className="ob-journey">
                {JOURNEY.map((j, i) => (
                  <li className="ob-journey__item" key={j.step}>
                    <div className="ob-journey__rail">
                      <span className="ob-journey__num">{i + 1}</span>
                      {i < JOURNEY.length - 1 && <span className="ob-journey__line" />}
                    </div>
                    <div className="ob-journey__card">
                      <div className="ob-journey__head">
                        <span className="ob-journey__icon"><j.icon size={18} /></span>
                        <div className="ob-journey__titles">
                          <span className="ob-journey__step">{t(j.step)}</span>
                          <span className="ob-journey__title">{t(j.title)}</span>
                        </div>
                        <Badge tone="neutral">{t(j.where)}</Badge>
                      </div>
                      <p className="ob-journey__text">{t(j.text)}</p>
                      <p className="ob-journey__analogy"><Icon.Sparkles size={13} /> {t(j.analogy)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {step === 'everyday' && (
            <div className="ob-step">
              <span className="ob-step-icon"><Icon.Boxes size={22} /></span>
              <h2 className="ob-title">{t('Your everyday tools')}</h2>
              <p className="ob-lead">
                {t('Not everything is a big order. These handle the day-to-day — all in the sidebar.')}
              </p>
              <div className="ob-concepts">
                {EVERYDAY.map((c) => (
                  <div className="ob-concept" key={c.title}>
                    <span className="ob-concept__icon"><c.icon size={20} /></span>
                    <div className="ob-concept__text">
                      <span className="ob-concept__title">{t(c.title)}</span>
                      <p>{t(c.text)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'cheatsheet' && (
            <div className="ob-step">
              <span className="ob-step-icon"><Icon.Zap size={22} /></span>
              <h2 className="ob-title">{t('If this happens… do that')}</h2>
              <p className="ob-lead">
                {t('Your pocket guide. You can reopen this tour any time from your avatar menu →')}
                <strong> {t('Getting started')}</strong>.
              </p>
              <div className="ob-cheats">
                {CHEATSHEET.map((c) => (
                  <div className="ob-cheat" key={c.when}>
                    <span className="ob-cheat__icon"><c.icon size={17} /></span>
                    <div className="ob-cheat__text">
                      <span className="ob-cheat__when">“{t(c.when)}”</span>
                      <span className="ob-cheat__then">
                        <Icon.ArrowRight size={13} /> {t(c.then)}
                        <Badge tone="neutral">{t(c.where)}</Badge>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ob-golden">
                <Icon.Sparkles size={16} />
                <p>
                  <strong>{t('Golden rule:')}</strong> {t('if it costs money, it starts with a requisition. When in doubt, raise one — the system routes it to the right person.')}
                </p>
              </div>
            </div>
          )}
        </div>

        <footer className="ob-foot">
          <div className="ob-foot__left">
            {stepIndex > 0 && (
              <Button variant="ghost" onClick={goBack} disabled={finishing} leadingIcon={<Icon.ArrowLeft size={16} />}>
                {t('Back')}
              </Button>
            )}
          </div>
          <div className="ob-foot__right">
            {!reviewMode && step === 'signature' && !hasSignature && (
              <span className="ob-foot__hint">{t('Save a signature to continue')}</span>
            )}
            {isLast ? (
              <Button variant="primary" onClick={finish} loading={finishing} leadingIcon={<Icon.Check size={16} />}>
                {reviewMode ? t('Done') : t('Finish setup')}
              </Button>
            ) : (
              <Button variant="primary" onClick={goNext} disabled={nextBlocked} trailingIcon={<Icon.ArrowRight size={16} />}>
                {t('Continue')}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
